/**
 * ProShip Direct — admin constants.
 *
 * ProShip Direct is the BYOC product: a merchant brings their own courier contracts and ships on
 * them through the ProShip platform for a subscription fee instead of per-shipment aggregator
 * fees. These screens are the Prozo side of the commercial relationship — the catalog sales
 * quotes from, the negotiated offers, and the live subscriptions.
 *
 * Backend: /api/direct/admin/** (ROLE_PROSHIPADMIN or SUPER_ADMIN), V10 schema.
 */

/** Every route and menu entry here is gated on this, matching the backend's @PreAuthorize. */
export const DIRECT_ROLE_GATING = ['ROLE_PROSHIPADMIN']

const ADMIN = 'direct/admin'

export const DIRECT_API = {
  PLANS: `${ADMIN}/plans`,
  PLAN: (id) => `${ADMIN}/plans/${id}`,
  PLAN_REPRICE: (id) => `${ADMIN}/plans/${id}/reprice`,

  OFFERS: `${ADMIN}/offers`,
  OFFER: (id) => `${ADMIN}/offers/${id}`,
  OFFER_PUBLISH: (id) => `${ADMIN}/offers/${id}/publish`,
  OFFER_WITHDRAW: (id) => `${ADMIN}/offers/${id}/withdraw`,

  SUBSCRIPTIONS: `${ADMIN}/subscriptions`,
  SUBSCRIPTION: (id) => `${ADMIN}/subscriptions/${id}`,
  SUB_LEDGER: (id) => `${ADMIN}/subscriptions/${id}/ledger`,
  SUB_CHARGES: (id) => `${ADMIN}/subscriptions/${id}/charges`,
  SUB_TERMS: (id) => `${ADMIN}/subscriptions/${id}/terms`,
  SUB_CANCEL: (id) => `${ADMIN}/subscriptions/${id}/cancel`,
  SUB_ACTIVATE: `${ADMIN}/subscriptions/activate`,
  SUB_RENEW: (id) => `${ADMIN}/subscriptions/${id}/renew`,

  MERCHANT_NOTIFICATIONS: (merchantOid) => `${ADMIN}/merchants/${merchantOid}/notifications`,
  JOB_DAILY: `${ADMIN}/jobs/daily`,

  DEFAULT_ACCOUNTS: `${ADMIN}/default-accounts`,
  DEFAULT_ACCOUNT_OPTIONS: `${ADMIN}/default-accounts/options`,
  DEFAULT_ACCOUNT_CANDIDATES: `${ADMIN}/default-accounts/candidates`,
  DEFAULT_ACCOUNT: (id) => `${ADMIN}/default-accounts/${id}`,
  DEFAULT_ACCOUNT_ENABLE: (id) => `${ADMIN}/default-accounts/${id}/enable`,
  DEFAULT_ACCOUNT_DISABLE: (id) => `${ADMIN}/default-accounts/${id}/disable`,
}

/** Statuses that hold the merchant's one live slot — the only ones the backend lets you cancel. */
export const LIVE_STATUSES = ['PENDING_PAYMENT', 'ACTIVE', 'GRACE', 'SUSPENDED']

export const AUDIENCE = { PUBLIC: 'PUBLIC', TARGETED: 'TARGETED' }

export const OFFER_STATUS = { DRAFT: 'DRAFT', PUBLISHED: 'PUBLISHED', WITHDRAWN: 'WITHDRAWN', EXPIRED: 'EXPIRED' }

export const PAYMENT_MODES = [
  { value: 'WALLET', label: 'Wallet (prepaid balance)' },
  { value: 'POSTPAID', label: 'Postpaid (credit line)' },
  { value: 'EXTERNAL', label: 'External (paid offline)' },
]

/** Status → chip colour. Unknown values fall through to default rather than crashing. */
export const statusColor = (s) => ({
  ACTIVE: 'success', PAID: 'success', ACCEPTED: 'success', PUBLISHED: 'success',
  GRACE: 'warning', PENDING_PAYMENT: 'warning', PENDING: 'warning', DRAFT: 'default',
  EXPIRED: 'error', FAILED: 'error', DECLINED: 'error', CANCELLED: 'error',
  SUSPENDED: 'error', WITHDRAWN: 'default', WAIVED: 'info', EXTERNAL: 'info',
}[s] || 'default')

export const inr = (n) =>
  n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

/**
 * NULL is a real value in this schema, not missing data:
 *   shipmentsIncluded null = unlimited shipments
 *   expiresAt         null = never expires, valid till the allowance is exhausted
 * Rendering either as a blank cell would be actively misleading, so they get words.
 */
export const unlimitedOr = (n, fmt = (v) => Number(v).toLocaleString('en-IN')) =>
  n === null || n === undefined ? 'Unlimited' : fmt(n)

export const expiryLabel = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : 'Till exhausted')

/** overdraftLimitUnits: 0 = hard stop, null = unlimited, n = n units past the allowance. */
export const overdraftLabel = (n) =>
  n === null || n === undefined ? 'Unlimited' : n === 0 ? 'Hard stop' : `${n} units`
